import useSWRMutation from 'swr/mutation'

import { useAuth } from '../../context/auth'
import { errorFromResponse, ErrorType, handleUnauthorized } from './useQuery'

const API_URL = import.meta.env.VITE_API_URL

/* eslint-disable @typescript-eslint/no-empty-object-type */
export function useMutation<
  ResponseData,
  InputFormat extends Record<string, unknown> | Record<string, unknown>[] | undefined = undefined,
  URLParams extends Record<string, string> | undefined = {},
  PathParams extends Record<string, string> | undefined = undefined,
>(path: string) {
  const authState = useAuth()

  // 1. Define the fetcher expected by useSWRMutation
  const fetcher = async (
    url: string,
    { arg }: { arg: { data?: InputFormat; urlParams?: URLParams; pathParams?: PathParams } },
  ) => {
    // Replace :param segments in the URL with actual values
    let resolvedPath = url
    if (arg.pathParams) {
      for (const [key, value] of Object.entries(arg.pathParams)) {
        resolvedPath = resolvedPath.replace(`:${key}`, encodeURIComponent(value))
      }
    }

    let finalUrl = API_URL + resolvedPath

    // Safely append URL parameters using URLSearchParams
    if (arg.urlParams) {
      const searchString = new URLSearchParams(arg.urlParams as Record<string, string>).toString()
      if (searchString) {
        finalUrl += (finalUrl.includes('?') ? '&' : '?') + searchString
      }
    }

    const response = await fetch(finalUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        UQRDM_TOKEN: authState?.token || '',
      },
      body: arg.data ? JSON.stringify(arg.data) : undefined,
    })

    if (!response.ok) {
      handleUnauthorized(response.status)
      throw await errorFromResponse(response, 'POST')
    }

    return (await response.json()) as ResponseData
  }

  // 2. Initialize the SWR mutation hook
  const { trigger, isMutating, error } = useSWRMutation(path, fetcher)

  // 3. Map SWR's native error back to your exact expected ErrorType shape
  const formattedError: ErrorType | null = error
    ? {
        code: error.code || 'UNKNOWN',
        message: error.message || 'An unknown error occurred',
      }
    : null

  // 4. Wrap the trigger function to precisely match your old `mutate` signature
  const mutate = (data?: InputFormat, urlParams?: URLParams, pathParams?: PathParams): Promise<ResponseData> => {
    return trigger({ data, urlParams, pathParams })
  }

  // 5. Return the exact same tuple format
  return [mutate, { loading: isMutating, error: formattedError }] as const
}
